// src/services/enhancers/BookingConflictEnhancer.js
// Enhancer following your established pattern for conflict-aware booking operations

import { ConflictResolutionService } from '../ConflictResolutionService';
import { ErrorTrackingService } from '../ErrorTrackingService';

export class BookingConflictEnhancer {
  
  // Enhanced createBooking with conflict resolution
  static async createBooking(businessId, bookingData, options = {}) {
    ErrorTrackingService.addBreadcrumb(
      'Conflict-aware booking creation started',
      'booking',
      'info',
      { 
        businessId, 
        service: bookingData.serviceName,
        hasConflictResolution: options.useConflictResolution !== false
      }
    );

    try {
      // Check if conflict resolution should be used
      const useConflictResolution = options.useConflictResolution !== false && 
        ConflictResolutionService.CONFLICT_RESOLUTION_ENABLED;
      
      let result;
      
      if (useConflictResolution) {
        // Use atomic conflict resolution
        result = await ConflictResolutionService.createBookingWithConflictResolution(
          businessId, 
          bookingData, 
          options
        );
        
        // Track conflict resolution usage
        ErrorTrackingService.addBreadcrumb(
          'Atomic booking creation completed',
          'booking',
          result.success ? 'info' : 'warning',
          { 
            businessId, 
            success: result.success,
            method: 'conflict_resolution',
            attempts: result.attempts,
            hasConflicts: result.conflicts?.length > 0
          }
        );
        
      } else {
        // Fallback to original BookingService method (preserves existing behavior)
        const { BookingService } = await import('../BookingService');
        result = await BookingService.createBooking(businessId, bookingData);
        
        ErrorTrackingService.addBreadcrumb(
          'Standard booking creation completed',
          'booking',
          result.success ? 'info' : 'warning',
          { 
            businessId, 
            success: result.success,
            method: 'standard',
            conflictResolutionDisabled: !ConflictResolutionService.CONFLICT_RESOLUTION_ENABLED
          }
        );
      }
      
      // Enhanced error tracking for booking failures
      if (!result.success) {
        ErrorTrackingService.captureError(
          new Error(`Booking creation failed: ${result.error}`),
          {
            service: 'BookingService',
            method: 'createBooking',
            enhancer: 'BookingConflictEnhancer',
            businessId,
            bookingData: {
              serviceName: bookingData.serviceName,
              customerName: bookingData.customerName,
              bookingDate: bookingData.bookingDate,
              time: bookingData.time
            },
            conflictInfo: {
              hasConflicts: result.conflicts?.length > 0,
              conflictCount: result.conflicts?.length || 0,
              suggestedAlternatives: result.suggestedAlternatives?.length || 0
            },
            level: 'warning'
          }
        );
      } else {
        // Track successful bookings
        ErrorTrackingService.addBreadcrumb(
          'Booking creation successful',
          'booking',
          'info',
          { 
            businessId, 
            bookingId: result.bookingId,
            method: useConflictResolution ? 'conflict_resolution' : 'standard'
          }
        );
      }
      
      return result;
      
    } catch (error) {
      // Enhanced error tracking with conflict context
      ErrorTrackingService.wrapServiceError(error, 'BookingService', 'createBooking', {
        enhancer: 'BookingConflictEnhancer',
        businessId,
        bookingData: {
          serviceName: bookingData.serviceName,
          customerName: bookingData.customerName,
          bookingDate: bookingData.bookingDate,
          time: bookingData.time
        },
        options: {
          useConflictResolution: options.useConflictResolution,
          conflictResolutionEnabled: ConflictResolutionService.CONFLICT_RESOLUTION_ENABLED
        }
      });
      
      throw error;
    }
  }

  // Enhanced updateBooking with conflict checking
  static async updateBooking(businessId, customerId, bookingId, updateData, options = {}) {
    ErrorTrackingService.addBreadcrumb(
      'Conflict-aware booking update started',
      'booking',
      'info',
      { businessId, bookingId, customerId }
    );

    try {
      const useConflictResolution = options.useConflictResolution !== false && 
        ConflictResolutionService.CONFLICT_RESOLUTION_ENABLED;
      
      let result;
      
      if (useConflictResolution && (updateData.bookingDate || updateData.time)) {
        // Check for conflicts if date/time is being updated
        result = await this.updateBookingWithConflictCheck(
          businessId, 
          customerId, 
          bookingId, 
          updateData
        );
      } else {
        // Use original update method
        const { BookingService } = await import('../BookingService');
        result = await BookingService.updateBooking(businessId, customerId, bookingId, updateData);
      }
      
      if (result.success) {
        ErrorTrackingService.addBreadcrumb(
          'Booking update successful',
          'booking',
          'info',
          { businessId, bookingId, useConflictResolution }
        );
      } else {
        ErrorTrackingService.captureError(
          new Error(`Booking update failed: ${result.error}`),
          {
            service: 'BookingService',
            method: 'updateBooking',
            enhancer: 'BookingConflictEnhancer',
            businessId,
            bookingId,
            customerId,
            level: 'warning'
          }
        );
      }
      
      return result;
      
    } catch (error) {
      ErrorTrackingService.wrapServiceError(error, 'BookingService', 'updateBooking', {
        enhancer: 'BookingConflictEnhancer',
        businessId,
        bookingId,
        customerId
      });
      
      throw error;
    }
  }

  // Update booking with conflict checking
  static async updateBookingWithConflictCheck(businessId, customerId, bookingId, updateData) {
    try {
      // Calculate new booking time if being updated
      let newDateTime = null;
      let duration = updateData.duration;
      
      if (updateData.bookingDate || updateData.time) {
        // Get current booking to determine new time
        const { BookingService } = await import('../BookingService');
        const { db } = await import('../FirebaseService').then(m => m.FirebaseService.getInstances());
        
        const bookingRef = await import('firebase/firestore').then(m => 
          m.doc(db, 'businesses', businessId, 'customers', customerId, 'bookings', bookingId)
        );
        const bookingSnap = await import('firebase/firestore').then(m => m.getDoc(bookingRef));
        
        if (!bookingSnap.exists()) {
          throw new Error('Booking not found');
        }
        
        const currentBooking = bookingSnap.data();
        const bookingDate = updateData.bookingDate || currentBooking.booking_date || currentBooking.date;
        const bookingTime = updateData.time || currentBooking.time;
        duration = duration || currentBooking.duration_minutes || 60;
        
        newDateTime = new Date(`${bookingDate}T${bookingTime}`);
        
        // Check for conflicts (excluding current booking)
        const availability = await ConflictResolutionService.checkTimeSlotAvailability(
          businessId,
          newDateTime.toISOString(),
          duration,
          bookingId // Exclude current booking
        );
        
        if (!availability.isAvailable) {
          return {
            success: false,
            error: 'Updated time slot conflicts with existing booking',
            conflicts: availability.conflicts,
            suggestedAlternatives: await ConflictResolutionService.suggestAlternativeTimeSlots(
              businessId,
              bookingDate,
              duration
            )
          };
        }
      }
      
      // Proceed with update using original method
      const { BookingService } = await import('../BookingService');
      return await BookingService.updateBooking(businessId, customerId, bookingId, updateData);
      
    } catch (error) {
      throw error;
    }
  }

  // Batch conflict validation for multiple bookings
  static async validateBookingConflicts(businessId, bookingsArray) {
    ErrorTrackingService.addBreadcrumb(
      'Batch conflict validation started',
      'booking',
      'info',
      { businessId, bookingCount: bookingsArray.length }
    );

    try {
      const conflictMatrix = [];
      
      for (let i = 0; i < bookingsArray.length; i++) {
        const booking = bookingsArray[i];
        const bookingDateTime = new Date(`${booking.bookingDate}T${booking.time}`);
        
        const availability = await ConflictResolutionService.checkTimeSlotAvailability(
          businessId,
          bookingDateTime.toISOString(),
          booking.duration || 60
        );
        
        conflictMatrix.push({
          index: i,
          booking: {
            customerName: booking.customerName,
            serviceName: booking.serviceName,
            dateTime: bookingDateTime.toISOString(),
            date: booking.bookingDate,
            time: booking.time
          },
          isAvailable: availability.isAvailable,
          conflicts: availability.conflicts,
          conflictCount: availability.conflicts.length
        });
      }
      
      const totalConflicts = conflictMatrix.reduce((sum, item) => sum + item.conflictCount, 0);
      
      ErrorTrackingService.addBreadcrumb(
        'Batch conflict validation completed',
        'booking',
        totalConflicts > 0 ? 'warning' : 'info',
        { 
          businessId, 
          bookingCount: bookingsArray.length,
          totalConflicts,
          conflictingBookings: conflictMatrix.filter(item => !item.isAvailable).length
        }
      );
      
      return {
        success: true,
        conflictMatrix,
        summary: {
          totalBookings: bookingsArray.length,
          availableBookings: conflictMatrix.filter(item => item.isAvailable).length,
          conflictingBookings: conflictMatrix.filter(item => !item.isAvailable).length,
          totalConflicts
        }
      };
      
    } catch (error) {
      ErrorTrackingService.captureError(error, {
        service: 'ConflictResolutionService',
        method: 'validateBookingConflicts',
        enhancer: 'BookingConflictEnhancer',
        businessId,
        bookingCount: bookingsArray.length
      });
      
      return {
        success: false,
        error: error.message,
        conflictMatrix: [],
        summary: {
          totalBookings: bookingsArray.length,
          availableBookings: 0,
          conflictingBookings: 0,
          totalConflicts: 0
        }
      };
    }
  }

  // Check time slot availability with enhanced error tracking
  static async checkTimeSlotAvailability(businessId, dateTime, duration = 60) {
    ErrorTrackingService.addBreadcrumb(
      'Time slot availability check requested',
      'booking',
      'info',
      { businessId, dateTime, duration }
    );

    try {
      const result = await ConflictResolutionService.checkTimeSlotAvailability(
        businessId,
        dateTime,
        duration
      );
      
      ErrorTrackingService.addBreadcrumb(
        'Time slot availability check completed',
        'booking',
        result.isAvailable ? 'info' : 'warning',
        { 
          businessId, 
          isAvailable: result.isAvailable,
          conflictCount: result.conflicts.length
        }
      );
      
      return result;
      
    } catch (error) {
      ErrorTrackingService.wrapServiceError(error, 'ConflictResolutionService', 'checkTimeSlotAvailability', {
        enhancer: 'BookingConflictEnhancer',
        businessId,
        dateTime,
        duration
      });
      
      throw error;
    }
  }

  // Integration with existing BookingServiceEnhancer
  static async integrateWithExistingEnhancer(businessId, bookingData, options = {}) {
    try {
      // First run through existing BookingServiceEnhancer for error tracking
      const { BookingServiceEnhancer } = await import('./BookingServiceEnhancer');
      
      // Then apply conflict resolution
      const enhancedOptions = {
        ...options,
        useConflictResolution: true,
        integratedEnhancer: true
      };
      
      return await this.createBooking(businessId, bookingData, enhancedOptions);
      
    } catch (error) {
      ErrorTrackingService.captureError(error, {
        service: 'BookingService',
        method: 'integrateWithExistingEnhancer',
        enhancer: 'BookingConflictEnhancer',
        businessId
      });
      
      throw error;
    }
  }

  // Get conflict resolution statistics
  static async getConflictResolutionStats(businessId, timeframe = 'week') {
    try {
      // This would query conflict resolution metrics from your error tracking
      // For now, return basic stats structure
      
      ErrorTrackingService.addBreadcrumb(
        'Conflict resolution statistics requested',
        'analytics',
        'info',
        { businessId, timeframe }
      );

      return {
        success: true,
        timeframe,
        stats: {
          totalBookingAttempts: 0,
          conflictResolutionUsed: 0,
          conflictsDetected: 0,
          conflictsResolved: 0,
          alternativeSuggestions: 0,
          lockTimeouts: 0,
          retryAttempts: 0
        },
        enabled: ConflictResolutionService.CONFLICT_RESOLUTION_ENABLED,
        generatedAt: new Date().toISOString()
      };
      
    } catch (error) {
      ErrorTrackingService.captureError(error, {
        service: 'ConflictResolutionService',
        method: 'getConflictResolutionStats',
        enhancer: 'BookingConflictEnhancer',
        businessId,
        timeframe
      });
      
      return {
        success: false,
        error: error.message,
        stats: null
      };
    }
  }
}